import {useContext, useState} from "react";
import {LockKeyhole, ShieldCheck} from "lucide-react";
import AuthContext from "../../context/authContext";
import {apiPost} from "../../shared/api/client";
import {useTranslation} from "../../shared/i18n";

export const LoginPage = () => {
    const {t} = useTranslation();
    const {startSession} = useContext(AuthContext);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [firstLoginToken, setFirstLoginToken] = useState("");
    const [credentials, setCredentials] = useState({username: "", password: ""});
    const [passwords, setPasswords] = useState({password: "", password_again: ""});

    const signIn = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError("");

        try {
            const response = await apiPost("/api/v2/public/users/login-admin", credentials);
            if (response.is_first) {
                setFirstLoginToken(response.token);
                return;
            }
            startSession(response.token);
        } catch (err) {
            setError(err?.response?.data?.message || t("auth.errors.signIn"));
        } finally {
            setLoading(false);
        }
    };

    const changePassword = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError("");

        try {
            await apiPost("/api/v2/admin/password", passwords, {
                headers: {Authorization: `Token ${firstLoginToken}`},
            });
            startSession(firstLoginToken);
        } catch (err) {
            setError(err?.response?.data?.message || t("auth.errors.changePassword"));
        } finally {
            setLoading(false);
        }
    };

    const isFirstLogin = Boolean(firstLoginToken);

    return (
        <main className="admin-login">
            <section className="admin-login-panel">
                <div className="admin-login-brand">
                    <div className="admin-brand-mark">S</div>
                    <div>
                        <p className="admin-brand-title">Sorpa Admin</p>
                        <p className="admin-brand-subtitle">{t("auth.brandSubtitle")}</p>
                    </div>
                </div>

                <div className="admin-login-copy">
                    <p className="admin-eyebrow">
                        {isFirstLogin ? t("auth.eyebrow.firstLogin") : t("auth.eyebrow.secure")}
                    </p>
                    <h1>{isFirstLogin ? t("auth.title.firstLogin") : t("auth.title.signIn")}</h1>
                    <p>{isFirstLogin ? t("auth.copy.firstLogin") : t("auth.copy.signIn")}</p>
                </div>

                <form className="admin-login-form" onSubmit={isFirstLogin ? changePassword : signIn}>
                    {error && <div className="admin-login-error">{error}</div>}
                    {isFirstLogin ? (
                        <>
                            <label className="admin-field">
                                <span>{t("auth.fields.newPassword")}</span>
                                <input
                                    type="password"
                                    value={passwords.password}
                                    onChange={(event) => setPasswords((value) => ({...value, password: event.target.value}))}
                                />
                            </label>
                            <label className="admin-field">
                                <span>{t("auth.fields.repeatPassword")}</span>
                                <input
                                    type="password"
                                    value={passwords.password_again}
                                    onChange={(event) => setPasswords((value) => ({...value, password_again: event.target.value}))}
                                />
                            </label>
                        </>
                    ) : (
                        <>
                            <label className="admin-field">
                                <span>{t("auth.fields.username")}</span>
                                <input
                                    autoComplete="username"
                                    value={credentials.username}
                                    onChange={(event) => setCredentials((value) => ({...value, username: event.target.value}))}
                                />
                            </label>
                            <label className="admin-field">
                                <span>{t("auth.fields.password")}</span>
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    value={credentials.password}
                                    onChange={(event) => setCredentials((value) => ({...value, password: event.target.value}))}
                                />
                            </label>
                        </>
                    )}
                    <button className="admin-button admin-button-primary" disabled={loading} type="submit">
                        {loading ? <ShieldCheck size={15} /> : <LockKeyhole size={15} />}
                        {isFirstLogin ? t("auth.submit.savePassword") : t("auth.submit.signIn")}
                    </button>
                </form>
            </section>
        </main>
    );
};
